import { useRef, useState, useEffect, useCallback } from "react";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import TurnstileWidget from "../components/TurnstileWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Lista de carreras ────────────────────────────────────────────────────────
const CARRERAS = [
  { value: "Medicina",                     label: "Medicina 👨‍⚕️🏥⚕️" },
  { value: "Enfermería",                   label: "Enfermería 🩺💉🩹" },
  { value: "Odontología",                  label: "Odontología 👩🏼‍⚕️🦷🩻" },
  { value: "Psicología",                   label: "Psicología 🧠💭" },
  { value: "Veterinaria",                  label: "Veterinaria 🐾🐕‍🦺" },
  { value: "Bioanálisis",                  label: "Bioanálisis 🩸🔬🧬" },
  { value: "Farmacia",                     label: "Farmacia 🥼💊" },
  { value: "Ingeniería Electromecánica",   label: "Ingeniería Electromecánica ⚙️🔧" },
  { value: "Ingeniería Industrial",        label: "Ingeniería Industrial 🏭📈" },
  { value: "Ingeniería Civil",             label: "Ingeniería Civil 🏗️" },
  { value: "Ingeniería en Software",       label: "Ingeniería en Software 👨🏽‍💻🌐📡" },
  { value: "Ingeniería en Ciberseguridad", label: "Ingeniería en Ciberseguridad 🔒🌐" },
  { value: "Hardware y Redes",             label: "Hardware y Redes 🖧💻" },
  { value: "Administración",               label: "Administración 📊💼" },
  { value: "Economía",                     label: "Economía 📈💰" },
  { value: "Contabilidad",                 label: "Contabilidad 🧾📉" },
  { value: "Mercadeo",                     label: "Mercadeo 📣🛍️" },
  { value: "Finanzas",                     label: "Finanzas 💹💳" },
  { value: "Educación",                    label: "Educación 📚👩‍🏫" },
  { value: "Lengua",                       label: "Lengua 🗣️📖" },
  { value: "Literatura",                   label: "Literatura 📘🖋️" },
  { value: "Matemáticas",                  label: "Matemáticas ➗📐" },
  { value: "Primaria",                     label: "Primaria 👶📒" },
  { value: "Secundaria",                   label: "Secundaria 👦📗" },
  { value: "Derecho",                      label: "Derecho ⚖️👨‍⚖️" },
  { value: "Química",                      label: "Química 🧪⚗️" },
  { value: "Biología",                     label: "Biología 🦠🔬" },
  { value: "Arquitectura",                 label: "Arquitectura 🏛️📏" },
];

// ─── Fondo animado ────────────────────────────────────────────────────────────
function ParticlesBG() {
  return (
    <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
      <circle cx="10%" cy="20%" r="2" fill="#F59E0B" opacity="0.7">
        <animate attributeName="cy" values="20%;80%;20%" dur="10s" repeatCount="indefinite" />
        <animate attributeName="cx" values="10%;15%;10%" dur="6s"  repeatCount="indefinite" />
      </circle>
      <circle cx="30%" cy="40%" r="3" fill="#46bb84" opacity="0.6">
        <animate attributeName="cy" values="40%;60%;40%" dur="8s"  repeatCount="indefinite" begin="2s" />
        <animate attributeName="cx" values="30%;25%;30%" dur="10s" repeatCount="indefinite" begin="2s" />
      </circle>
      <circle cx="70%" cy="30%" r="2.5" fill="#34d399" opacity="0.5">
        <animate attributeName="cy" values="30%;50%;30%" dur="12s" repeatCount="indefinite" begin="1s" />
        <animate attributeName="cx" values="70%;65%;70%" dur="8s"  repeatCount="indefinite" begin="1s" />
      </circle>
      <circle cx="85%" cy="75%" r="2" fill="#a3e635" opacity="0.4">
        <animate attributeName="cy" values="75%;85%;75%" dur="9s"  repeatCount="indefinite" begin="3s" />
        <animate attributeName="cx" values="85%;80%;85%" dur="7s"  repeatCount="indefinite" begin="3s" />
      </circle>
    </svg>
  );
}

// ─── Pantalla de verificación pendiente ──────────────────────────────────────
function PendingScreen({ nombre, onGoToLogin }) {
  return (
    <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-md mx-auto space-y-6">
      <div className="text-7xl animate-bounce">⏳</div>
      <h2 className="text-3xl font-extrabold text-yellow-400 drop-shadow-lg">¡Registro completado!</h2>
      <p className="text-white text-lg font-medium">
        Hola, <span className="text-yellow-300 font-bold">{nombre}</span>. Tu cuenta ha sido creada exitosamente.
      </p>
      <div className="bg-white bg-opacity-10 backdrop-blur-md border border-white border-opacity-20 rounded-2xl p-6 space-y-3 text-left w-full">
        <p className="text-green-200 text-sm leading-relaxed">
          🔍 <strong className="text-white">Tu cuenta está siendo verificada.</strong> Nuestro equipo revisará la información que proporcionaste para validar tu acceso completo a la plataforma.
        </p>
        <p className="text-green-200 text-sm leading-relaxed">
          ⏱️ Este proceso tiene un tiempo estimado de{" "}
          <span className="text-yellow-300 font-semibold">1 a 6 horas</span> en días hábiles.
        </p>
        <p className="text-green-200 text-sm leading-relaxed">
          📖 Mientras tanto, podrás <strong className="text-white">explorar y leer</strong> el contenido de la plataforma, pero las funciones de publicación e interacción estarán habilitadas una vez que tu cuenta sea aprobada.
        </p>
        <p className="text-green-200 text-sm leading-relaxed">
          ✅ Una vez verificado, tendrás acceso completo a todas las funciones de{" "}
          <span className="text-yellow-300 font-semibold">Social Interactive Technology</span>.
        </p>
      </div>
      <button
        onClick={onGoToLogin}
        className="w-full bg-gradient-to-r from-yellow-400 via-green-400 to-teal-500 text-white font-semibold py-3 rounded-xl shadow-lg hover:from-yellow-500 hover:via-green-500 hover:to-teal-600 transition"
      >
        Ir al inicio de sesión
      </button>
    </div>
  );
}

// ─── Modal OTP — verificación de correo ──────────────────────────────────────
function EmailVerificationModal({ isOpen, email, onVerified, onClose, onResend }) {
  const [digits,         setDigits]         = useState(["", "", "", "", "", ""]);
  const [codeLoading,    setCodeLoading]    = useState(false);
  const [codeError,      setCodeError]      = useState("");
  const [countdown,      setCountdown]      = useState(600);
  const [resendCooldown, setResendCooldown] = useState(60);
  const [canResend,      setCanResend]      = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!isOpen) return;
    setDigits(["", "", "", "", "", ""]);
    setCodeError("");
    setCountdown(600);
    setResendCooldown(60);
    setCanResend(false);
    setTimeout(() => inputRefs.current[0]?.focus(), 120);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const t = setInterval(() =>
      setCountdown(prev => (prev <= 1 ? (clearInterval(t), 0) : prev - 1)), 1000);
    return () => clearInterval(t);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || canResend) return;
    const t = setInterval(() =>
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(t); setCanResend(true); return 0; }
        return prev - 1;
      }), 1000);
    return () => clearInterval(t);
  }, [isOpen, canResend]);

  const fmt = (s) =>
    `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

  const maskEmail = (e) => {
    const [u, d] = e.split("@");
    return `${u.slice(0, 3)}${"*".repeat(Math.max(2, u.length - 3))}@${d}`;
  };

  const submitCode = useCallback(async (fullCode) => {
    if (fullCode.length !== 6 || codeLoading) return;
    setCodeLoading(true);
    setCodeError("");
    try {
      const res  = await fetch(`${BACKEND_URL}/api/auth/verify-email-code`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email, code: fullCode }),
      });
      const data = await res.json();
      if (data.success) {
        onVerified();
      } else {
        setCodeError(data.error || "Código incorrecto.");
        setDigits(["", "", "", "", "", ""]);
        setTimeout(() => inputRefs.current[0]?.focus(), 60);
      }
    } catch {
      setCodeError("Error de conexión. Intenta de nuevo.");
    }
    setCodeLoading(false);
  }, [email, codeLoading, onVerified]);

  const handleDigitChange = (i, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...digits];
    next[i] = value.slice(-1);
    setDigits(next);
    setCodeError("");
    if (value && i < 5) inputRefs.current[i + 1]?.focus();
    if (i === 5 && value) {
      const full = [...next.slice(0, 5), value].join("");
      if (full.length === 6) submitCode(full);
    }
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputRefs.current[i - 1]?.focus();
    if (e.key === "ArrowLeft"  && i > 0) inputRefs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 5) inputRefs.current[i + 1]?.focus();
    if (e.key === "Enter") submitCode(digits.join(""));
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(""));
      inputRefs.current[5]?.focus();
      submitCode(pasted);
    }
    e.preventDefault();
  };

  const handleResend = async () => {
    setCanResend(false);
    setResendCooldown(60);
    setDigits(["", "", "", "", "", ""]);
    setCodeError("");
    setCountdown(600);
    await onResend();
    setTimeout(() => inputRefs.current[0]?.focus(), 120);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex justify-center items-center z-50 px-4">
      <div
        className="w-full max-w-sm overflow-hidden rounded-2xl shadow-2xl border border-white/10"
        style={{ background: "linear-gradient(160deg,#1a2f3a 0%,#0f2027 100%)" }}
      >
        {/* Cabecera */}
        <div
          className="px-6 py-5 border-b border-white/10 flex items-start justify-between"
          style={{ background: "linear-gradient(135deg,rgba(32,58,67,.8),rgba(44,83,100,.8))" }}
        >
          <div>
            <h3 className="text-white font-bold text-lg leading-tight">Verifica tu correo</h3>
            <p className="text-green-300 text-sm mt-0.5">
              Código enviado a{" "}
              <span className="text-yellow-300 font-semibold">{maskEmail(email)}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/30 hover:text-white/70 text-2xl font-light leading-none transition ml-3 mt-0.5"
          >×</button>
        </div>

        {/* Cuerpo */}
        <div className="px-6 py-6 space-y-5">
          {/* Temporizador */}
          <div className="text-center">
            <span className={`text-3xl font-black tabular-nums ${countdown < 60 ? "text-red-400" : "text-yellow-400"}`}>
              {fmt(countdown)}
            </span>
            <p className="text-white/30 text-xs mt-1">tiempo restante</p>
          </div>

          {/* Cajas de dígitos */}
          <div className="flex gap-2 justify-center" onPaste={handlePaste}>
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                type="text"
                inputMode="numeric"
                pattern="\d*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleDigitChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                disabled={codeLoading || countdown === 0}
                className={[
                  "w-11 text-center text-xl font-black rounded-xl border-2 bg-white/10 text-white",
                  "focus:outline-none transition-all disabled:opacity-40",
                  codeError ? "border-red-400" : digit ? "border-yellow-400" : "border-white/20 focus:border-yellow-400/70",
                ].join(" ")}
                style={{ height: "3rem" }}
              />
            ))}
          </div>

          {codeError && (
            <p className="text-center text-red-400 text-sm font-medium">{codeError}</p>
          )}
          {countdown === 0 && !codeError && (
            <p className="text-center text-red-400 text-sm">El código expiró. Solicita uno nuevo.</p>
          )}

          {/* Confirmar */}
          <button
            onClick={() => submitCode(digits.join(""))}
            disabled={codeLoading || digits.some((d) => !d) || countdown === 0}
            className="w-full py-3 rounded-xl font-bold text-black transition disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "linear-gradient(135deg,#F59E0B,#46bb84)" }}
          >
            {codeLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Verificando...
              </span>
            ) : "Confirmar código"}
          </button>

          {/* Reenviar */}
          <div className="text-center">
            {canResend ? (
              <button onClick={handleResend} className="text-green-300 text-sm hover:text-green-200 underline underline-offset-2 transition">
                Reenviar código
              </button>
            ) : (
              <p className="text-white/35 text-sm">
                Reenviar en <span className="text-white/55 font-semibold tabular-nums">{resendCooldown}s</span>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
function Register() {
  const [nombre,        setNombre]        = useState("");
  const [email,         setEmail]         = useState("");
  const [password,      setPassword]      = useState("");
  const [role,          setRole]          = useState("");
  const [carrera,       setCarrera]       = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal,setShowTermsModal]= useState(false);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState("");
  const [registered,    setRegistered]    = useState(false);
  const [turnstileToken,setTurnstileToken]= useState("");
  const [turnstileKey,  setTurnstileKey]  = useState(0);

  // ── OTP ──
  const [emailVerified,  setEmailVerified]  = useState(false);
  const [showCodeModal,  setShowCodeModal]  = useState(false);
  const [sendingCode,    setSendingCode]    = useState(false);

  // ── Contraseña visible ──
  const [showPassword,   setShowPassword]   = useState(false);

  const navigate = useNavigate();

  // ── Enviar código OTP ────────────────────────────────────────────────────
  const handleSendCode = async () => {
    if (!email) return setError("Ingresa tu correo primero.");
    setSendingCode(true);
    setError("");
    try {
      const res  = await fetch(`${BACKEND_URL}/api/auth/send-verification-code`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email: email.toLowerCase().trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Error al enviar el código.");
      } else {
        setShowCodeModal(true);
      }
    } catch {
      setError("Error de conexión. Verifica tu internet e intenta de nuevo.");
    }
    setSendingCode(false);
  };

  const handleCodeVerified = () => {
    setEmailVerified(true);
    setShowCodeModal(false);
  };

  const handleResendCode = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/send-verification-code`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email: email.toLowerCase().trim() }),
      });
    } catch { /* silencioso — el modal ya muestra el timer */ }
  };

  // ── Submit registro ──────────────────────────────────────────────────────
  const registerUser = async (e) => {
    e.preventDefault();
    setError("");

    if (!emailVerified)        return setError("Primero debes verificar tu correo.");
    if (!nombre.trim())        return setError("Ingresa tu nombre completo.");
    if (!role)                 return setError("Selecciona tu rol.");
    if (!carrera)              return setError("Selecciona tu carrera.");
    if (password.length < 6)   return setError("La contraseña debe tener al menos 6 caracteres.");

    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email, password, nombre: nombre.trim(), role, carrera, turnstileToken }),
      });
      const data = await res.json();

      if (!res.ok) {
        setTurnstileToken("");
        setTurnstileKey((k) => k + 1);
        setError(data.error || "Error al crear la cuenta.");
        setLoading(false);
        return;
      }

      await signInWithCustomToken(auth, data.customToken);
      setRegistered(true);
    } catch (err) {
      console.error(err);
      setError("Error de conexión. Verifica tu internet e inténtalo de nuevo.");
    }
    setLoading(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-tr from-[#0f2027] via-[#203a43] to-[#2c5364] overflow-hidden px-4 sm:px-6 lg:px-8 text-white py-10">
      <ParticlesBG />

      {registered ? (
        <PendingScreen nombre={nombre} onGoToLogin={() => navigate("/login")} />
      ) : (
        <form
          onSubmit={registerUser}
          className="relative z-10 bg-white bg-opacity-10 backdrop-blur-md rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-md mx-auto space-y-5 border border-white border-opacity-20"
          style={{ animation: "fadeSlideIn 1s ease forwards" }}
        >
          {/* Logo */}
          <h2
            className="text-2xl sm:text-3xl font-extrabold text-center text-yellow-400 drop-shadow-lg"
            style={{ animation: "subtleMove 4s ease-in-out infinite" }}
          >
            SIT
          </h2>
          <p className="text-center text-green-300 italic text-sm sm:text-base drop-shadow-md">
            Registro Beta — Social Interactive Technology
          </p>

          {/* Error global */}
          {error && (
            <div className="bg-red-500 bg-opacity-20 border border-red-400 text-red-200 text-sm rounded-xl px-4 py-2">
              {error}
            </div>
          )}

          {/* Nombre */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-green-200">Nombre completo</label>
            <input
              type="text"
              className="w-full bg-white bg-opacity-20 text-white border border-transparent focus:border-yellow-400 placeholder-white placeholder-opacity-60 p-3 rounded-xl transition"
              placeholder="Tu nombre completo"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              required
            />
            <p className="text-white/40 text-xs leading-snug pt-0.5 flex items-start gap-1">
              <span className="mt-px shrink-0">ⓘ</span>
              <span>Usa tu nombre real, no apodos ni sobrenombres. Las cuentas con nombres falsos podrían no ser verificadas.</span>
            </p>
          </div>

          {/* Correo + botón verificar */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-green-200">Correo electrónico</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                className="w-full sm:flex-1 bg-white bg-opacity-20 text-white border border-transparent focus:border-yellow-400 placeholder-white placeholder-opacity-60 p-3 rounded-xl transition disabled:opacity-60"
                placeholder="ejemplo@correo.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailVerified(false); }}
                required
                disabled={emailVerified}
              />
              <button
                type="button"
                onClick={handleSendCode}
                disabled={sendingCode || emailVerified || !email}
                className={[
                  "w-full sm:w-auto px-4 py-3 rounded-xl font-semibold text-sm transition flex items-center justify-center gap-1.5",
                  emailVerified
                    ? "bg-green-500 text-white cursor-default"
                    : "bg-yellow-400 text-black hover:bg-yellow-500 disabled:opacity-50",
                ].join(" ")}
              >
                {emailVerified ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    Verificado
                  </>
                ) : sendingCode ? (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : "Verificar"}
              </button>
            </div>
            {emailVerified && (
              <p className="text-green-400 text-xs flex items-center gap-1 mt-1">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Correo verificado con éxito
              </p>
            )}
          </div>

          {/* Contraseña */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-green-200">Contraseña</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                className="w-full bg-white bg-opacity-20 text-white border border-transparent focus:border-yellow-400 placeholder-white placeholder-opacity-60 p-3 pr-12 rounded-xl transition"
                placeholder="Mínimo 6 caracteres"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition"
                tabIndex={-1}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Rol */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-green-200">Eres...</label>
            <select
              className="w-full bg-[#1a3244] text-white border border-transparent focus:border-yellow-400 p-3 rounded-xl transition"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              required
            >
              <option value="" disabled>Selecciona tu rol</option>
              <option value="Estudiante">🎓 Estudiante</option>
              <option value="Docente">👨‍🏫 Docente</option>
            </select>
          </div>

          {/* Carrera */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-green-200">Carrera</label>
            <select
              className="w-full bg-[#1a3244] text-white border border-transparent focus:border-yellow-400 p-3 rounded-xl transition"
              value={carrera}
              onChange={(e) => setCarrera(e.target.value)}
              required
            >
              <option value="" disabled>Selecciona tu carrera</option>
              {CARRERAS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Términos */}
          <div className="flex items-start space-x-2 text-sm text-green-200">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={() => setAcceptedTerms(!acceptedTerms)}
              className="mt-1 accent-yellow-400"
            />
            <label className="select-none">
              Leí y acepto los{" "}
              <span className="underline text-yellow-300 cursor-pointer" onClick={() => setShowTermsModal(true)}>
                términos y condiciones
              </span>
            </label>
          </div>

          {/* Turnstile */}
          <TurnstileWidget
            key={turnstileKey}
            onVerify={(token) => setTurnstileToken(token)}
            onExpire={() => setTurnstileToken("")}
            theme="dark"
          />

          {/* Botón submit */}
          <button
            type="submit"
            className="w-full bg-gradient-to-r from-yellow-400 via-green-400 to-teal-500 text-white font-semibold py-3 rounded-xl shadow-lg transition hover:from-yellow-500 hover:via-green-500 hover:to-teal-600 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !acceptedTerms || password.length < 6 || !turnstileToken || !emailVerified}
          >
            {loading ? "Creando cuenta..." : "Crear cuenta"}
          </button>

          {/* Banner perfil promocional */}
          <div
            onClick={() => navigate("/perfil-promocional")}
            className="cursor-pointer rounded-xl border border-white border-opacity-10 bg-gradient-to-r from-purple-900/30 to-indigo-900/30 hover:from-purple-900/45 hover:to-indigo-900/45 p-3 flex items-center gap-3 transition group"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && navigate("/perfil-promocional")}
          >
            <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-base shadow-md shadow-purple-900/40">
              🏷️
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-purple-300 leading-none mb-0.5">¿Tienes un negocio?</p>
              <p className="text-xs text-white/55 leading-snug truncate">
                Llega a cientos de estudiantes con tu perfil promocional
              </p>
            </div>
            <span className="text-purple-400 group-hover:text-purple-300 text-sm flex-shrink-0 transition">→</span>
          </div>

          <p
            onClick={() => navigate("/login")}
            className="text-yellow-300 text-center text-sm cursor-pointer hover:underline select-none"
          >
            ¿Ya tienes cuenta? Inicia sesión
          </p>
        </form>
      )}

      {/* Modal de términos */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex justify-center items-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-yellow-400 px-6 py-4 flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <h3 className="text-lg font-extrabold text-black">Términos, Condiciones y Aviso Legal</h3>
            </div>
            <div className="px-6 py-5 space-y-4 text-sm text-gray-700 max-h-[60vh] overflow-y-auto">
              <div>
                <p className="font-semibold text-gray-900 mb-1">📋 Uso de la plataforma</p>
                <p>Al registrarte, aceptas cumplir con las normas de uso del sistema SIT. No compartas tus credenciales y respeta la integridad de los datos y los demás usuarios.</p>
              </div>
              <hr className="border-gray-200" />
              <div>
                <p className="font-semibold text-gray-900 mb-1">⚠️ Aviso Importante</p>
                <p className="mb-2">
                  <strong>SIT (Social Interactive Technology)</strong> es una plataforma digital independiente en fase beta, desarrollada con fines de prueba, validación y mejora continua.
                </p>
                <p className="mb-2">
                  Esta plataforma <strong>no está afiliada, asociada ni respaldada oficialmente</strong> por ninguna institución educativa, incluyendo universidades donde puedan encontrarse usuarios registrados.
                </p>
                <p className="mb-2">
                  El uso de SIT es completamente <strong>voluntario</strong>. Cada usuario es responsable del contenido que publica, comparte o comunica dentro de la plataforma.
                </p>
                <p className="mb-2">
                  SIT cuenta con sistemas de <strong>moderación, monitoreo y reporte</strong> para mantener un entorno seguro y respetuoso. El uso indebido puede conllevar la suspensión o eliminación de cuentas.
                </p>
                <p>
                  Al registrarse y utilizar SIT, el usuario acepta estos términos y se compromete a hacer un <strong>uso responsable</strong> de la plataforma.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100">
              <button
                className="w-full bg-teal-600 text-white px-4 py-2.5 rounded-xl hover:bg-teal-700 transition font-semibold"
                onClick={() => setShowTermsModal(false)}
              >
                Entendido y acepto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal OTP */}
      <EmailVerificationModal
        isOpen={showCodeModal}
        email={email}
        onVerified={handleCodeVerified}
        onClose={() => setShowCodeModal(false)}
        onResend={handleResendCode}
      />

      <style>{`
        @keyframes fadeSlideIn {
          0%   { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes subtleMove {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(5px); }
        }
      `}</style>
    </div>
  );
}

export default Register;
